/**
 * Precios DIARIOS oficiales del Mercado Agroganadero (Cañuelas): agrupar por
 * día, subtotal por categoría y variación contra el día de mercado anterior.
 * Funciones puras (web, render estático del build y tests).
 * Datos: tabla precios_mag_diarios (migración 20260929110000), RPC
 * precios_mag_ultimos_dias y snapshot public/mag-snapshot.json.
 */

/** Fila tal como la devuelve la base (snake_case). */
export interface FilaMagDb {
  fecha: string; // YYYY-MM-DD
  categoria: string;
  subcategoria: string; // '' = subtotal de la categoría
  minimo: number | null;
  maximo: number | null;
  promedio: number;
  mediana: number | null;
  cabezas: number | null;
  kgs_promedio: number | null;
  provisorio: boolean;
}

export interface MagSnapshot {
  generatedAt: string;
  filas: FilaMagDb[];
}

export interface CategoriaDelDia {
  categoria: string;
  label: string;
  /** Subtotal oficial de la categoría (promedio ponderado del mercado). */
  total: FilaMagDb | null;
  subcategorias: FilaMagDb[];
  /** % contra el subtotal de la misma categoría el último día anterior en que tuvo precio. */
  variacion: number | null;
  /** Día del dato (en el precio vigente puede ser anterior al día mostrado). */
  fecha: string;
  provisorio: boolean;
}

export interface DiaMag {
  fecha: string;
  provisorio: boolean;
  categorias: CategoriaDelDia[];
}

export const ORDEN_CATEGORIAS_MAG = ['NOVILLOS', 'NOVILLITOS', 'VAQUILLONAS', 'VACAS', 'TOROS', 'MEJ'];

export const LABEL_CATEGORIA_MAG: Record<string, string> = {
  NOVILLOS: 'Novillos',
  NOVILLITOS: 'Novillitos',
  VAQUILLONAS: 'Vaquillonas',
  VACAS: 'Vacas',
  TOROS: 'Toros',
  MEJ: 'MEJ (macho entero joven)',
};

export const FUENTE_MAG = {
  nombre: 'Mercado Agroganadero S.A. (Cañuelas)',
  url: 'https://www.mercadoagroganadero.com.ar/dll/hacienda1.dll/haciinfo000502',
};

/** Normaliza números que PostgREST puede devolver como string (numeric). */
const num = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));

export function normalizarFilas(filas: unknown[]): FilaMagDb[] {
  return (filas as Record<string, unknown>[]).map((f) => ({
    fecha: String(f.fecha).slice(0, 10),
    categoria: String(f.categoria),
    subcategoria: String(f.subcategoria ?? ''),
    minimo: num(f.minimo),
    maximo: num(f.maximo),
    promedio: Number(f.promedio),
    mediana: num(f.mediana),
    cabezas: num(f.cabezas),
    kgs_promedio: num(f.kgs_promedio),
    provisorio: Boolean(f.provisorio),
  }));
}

/**
 * Agrupa las filas por día (más nuevo primero), con subtotal y variación por
 * categoría. Ignora filas sin precio (el mercado a veces publica días o
 * categorías sin operaciones).
 */
export function agruparDiasMag(filas: FilaMagDb[]): DiaMag[] {
  const porFecha = new Map<string, FilaMagDb[]>();
  for (const f of filas) {
    if (!(f.promedio > 0)) continue;
    if (!porFecha.has(f.fecha)) porFecha.set(f.fecha, []);
    porFecha.get(f.fecha)!.push(f);
  }
  const fechas = [...porFecha.keys()].sort().reverse();

  const totalDe = (fecha: string, categoria: string) =>
    porFecha.get(fecha)?.find((f) => f.categoria === categoria && f.subcategoria === '') ?? null;

  return fechas.map((fecha, i) => {
    const delDia = porFecha.get(fecha)!;
    const categorias = ORDEN_CATEGORIAS_MAG.filter((c) => delDia.some((f) => f.categoria === c)).map(
      (categoria): CategoriaDelDia => {
        const total = totalDe(fecha, categoria);
        // Día anterior en que la categoría tuvo precio (no necesariamente el día hábil anterior).
        const fechaPrevia = fechas.slice(i + 1).find((f) => totalDe(f, categoria));
        const previo = fechaPrevia ? totalDe(fechaPrevia, categoria) : null;
        const subcategorias = delDia.filter((f) => f.categoria === categoria && f.subcategoria !== '');
        return {
          categoria,
          label: LABEL_CATEGORIA_MAG[categoria] ?? categoria,
          total,
          subcategorias,
          variacion:
            total && previo ? Math.round(((total.promedio - previo.promedio) / previo.promedio) * 1000) / 10 : null,
          fecha,
          provisorio: [total, ...subcategorias].some((f) => f?.provisorio),
        };
      },
    );
    return { fecha, provisorio: delDia.some((f) => f.provisorio), categorias };
  });
}

/**
 * Precio vigente para mostrar "hoy": el último día con datos y, si ese día una
 * categoría no tuvo precio, la categoría se completa con su último día con
 * datos (cada categoría conserva su `fecha`). Null si no hay ningún dato.
 */
export function precioVigenteMag(dias: DiaMag[]): DiaMag | null {
  const [ultimo] = dias;
  if (!ultimo) return null;
  const tienePrecio = (c: CategoriaDelDia) => c.total !== null || c.subcategorias.length > 0;
  const categorias = ORDEN_CATEGORIAS_MAG.map((categoria) => {
    for (const dia of dias) {
      const c = dia.categorias.find((x) => x.categoria === categoria);
      if (c && tienePrecio(c)) return c;
    }
    return null;
  }).filter((c): c is CategoriaDelDia => c !== null);
  return { fecha: ultimo.fecha, provisorio: ultimo.provisorio, categorias };
}

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** "2026-09-28" → "lunes 28/09/2026" (sin depender de la zona horaria). */
export function fechaLargaMag(iso: string): string {
  const [a, m, d] = iso.split('-').map(Number);
  const dia = DIAS[new Date(Date.UTC(a, m - 1, d)).getUTCDay()];
  return `${dia} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${a}`;
}

/**
 * El snapshot o la base: se queda con el que tenga el día de mercado más nuevo.
 * A igual fecha gana `b` (la fuente que llegó después: corrige los provisorios).
 */
export function masReciente(a: FilaMagDb[], b: FilaMagDb[]): FilaMagDb[] {
  const maxA = a.reduce((m, f) => (f.fecha > m ? f.fecha : m), '');
  const maxB = b.reduce((m, f) => (f.fecha > m ? f.fecha : m), '');
  return b.length && maxB >= maxA ? b : a;
}
